import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import adminService from '../../services/adminService';
import ConfirmationModal from '../ConfirmationModal';

const UserManagementTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'tpb_customer',
    organization_name: '',
    organization_description: '',
    password: '',
    password_confirm: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const users = await adminService.getUsers();
      setUsers(users);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.error || err.message;
      setError(`Failed to load users: ${errorMsg}`);
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        role: user.role,
        organization_name: '',
        organization_description: '',
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        role: 'tpb_customer',
        organization_name: '',
        organization_description: '',
        password: '',
        password_confirm: '',
      });
    }
    setOpenDialog(true);
    setError('');
    setSuccess('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSaveUser = async () => {
    setError('');
    setSuccess('');

    if (!formData.email || !formData.first_name) {
      setError('Email and first name are required');
      return;
    }

    // For new users, password is required
    if (!editingUser) {
      if (!formData.password || !formData.password_confirm) {
        setError('Password and password confirmation are required');
        return;
      }
      if (formData.password !== formData.password_confirm) {
        setError('Passwords do not match');
        return;
      }
    }

    try {
      setSubmitting(true);

      if (editingUser) {
        if (formData.password) {
          if (formData.password !== formData.password_confirm) {
            setError('Passwords do not match');
            setSubmitting(false);
            return;
          }
        }
        
        // Update user
        const updateData = {
          email: formData.email,
          first_name: formData.first_name,
          last_name: formData.last_name,
          role: formData.role,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await adminService.updateUser(editingUser.id, updateData);
        setSuccess('User updated successfully');
      } else {
        // Create user - send all fields including passwords
        await adminService.createUser(formData);
        setSuccess('User created successfully');
      }

      handleCloseDialog();
      fetchUsers();
    } catch (err) {
      let errorMsg = 'Failed to save user';
      if (err.response && err.response.data) {
        if (typeof err.response.data === 'object') {
          // Extract validation errors
          errorMsg = Object.entries(err.response.data)
            .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
            .join(' | ');
        } else {
          errorMsg = String(err.response.data);
        }
      } else if (err.message) {
        errorMsg = err.message;
      }
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    const user = users.find(u => u.id === userId);
    setUserToDelete(user);
    setDeleteModalOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      setDeleteLoading(true);
      setError('');
      setSuccess('');
      await adminService.deleteUser(userToDelete.id);
      setSuccess('User deleted successfully!');
      setDeleteModalOpen(false);
      setUserToDelete(null);
      // Refresh the user list after delete
      await fetchUsers();
    } catch (err) {
      console.error('Delete error:', err);
      const errorMsg = err.response?.data?.detail || err.response?.data?.error || 'Failed to delete user';
      setError(errorMsg);
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setUserToDelete(null);
  };

  const getRoleChip = (role) => {
    const colors = {
      applicant: 'default',
      admin: 'primary',
      super_admin: 'error',
      superadmin: 'error',
      tpb: 'info',
      system_admin: 'secondary',
    };

    const labels = {
      applicant: 'Applicant',
      admin: 'Admin',
      super_admin: 'SuperAdmin',
      superadmin: 'SuperAdmin',
      tpb: 'TPB',
      system_admin: 'System Admin',
    };

    return (
      <Chip
        label={labels[role] || role}
        color={colors[role]}
        size="small"
        variant="outlined"
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Users ({users.length})</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Role</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography color="text.secondary">No users found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    {user.first_name} {user.last_name}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{getRoleChip(user.role)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenDialog(user)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteUser(user.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* User Form Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <TextField
            fullWidth
            label="Email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            margin="normal"
            disabled={editingUser}
          />

          <TextField
            fullWidth
            label="First Name"
            name="first_name"
            value={formData.first_name}
            onChange={handleInputChange}
            margin="normal"
          />

          <TextField
            fullWidth
            label="Last Name"
            name="last_name"
            value={formData.last_name}
            onChange={handleInputChange}
            margin="normal"
          />

          <TextField
            fullWidth
            label="Password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleInputChange}
            margin="normal"
            required={!editingUser}
            helperText={editingUser ? "Leave blank to keep current password" : ""}
          />

          <TextField
            fullWidth
            label="Confirm Password"
            name="password_confirm"
            type="password"
            value={formData.password_confirm}
            onChange={handleInputChange}
            margin="normal"
            required={!editingUser}
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              label="Role"
            >
              <MenuItem value="tpb_customer">TPB Customer (Applicant)</MenuItem>
              <MenuItem value="tpb_staff">TPB Staff</MenuItem>
              <MenuItem value="tpb_manager">TPB Manager</MenuItem>
              <MenuItem value="system_admin">System Admin</MenuItem>
            </Select>
          </FormControl>

          {formData.role === 'tpb_manager' && (
            <>
              <TextField
                fullWidth
                label="Organization Name"
                name="organization_name"
                value={formData.organization_name || ''}
                onChange={handleInputChange}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Organization Description"
                name="organization_description"
                value={formData.organization_description || ''}
                onChange={handleInputChange}
                margin="normal"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveUser}
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : (editingUser ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={deleteModalOpen}
        title="Delete User"
        message="Are you sure you want to delete this user?"
        subMessage={
          userToDelete
            ? `${userToDelete.first_name || ''} ${userToDelete.last_name || ''}`.trim() ||
              userToDelete.email
            : ''
        }
        onConfirm={confirmDeleteUser}
        onCancel={cancelDelete}
        loading={deleteLoading}
        confirmText="Delete user"
        cancelText="Cancel"
        confirmColor="error"
        icon="delete"
      />
    </Paper>
  );
};

export default UserManagementTab;
